/* Where this tenant's alerts go. D-17, built to the reference design.
 *
 * The reference is six stacked sections, and the order is the argument: the headline says whether a
 * critical incident reaches anybody at all, then coverage is broken out **per kind**, then the rules
 * themselves, then a replay of what those rules would have done to incidents already on the queue.
 * Somebody arriving after an alert nobody received reads it top to bottom and stops as soon as they
 * have their answer.
 *
 * Four things the screen is deliberate about, and every one is a statement about a customer's
 * alerting rather than a layout choice.
 *
 * It **distinguishes the default from a decision.** `declared: false` means the rules below are the
 * platform's own — critical incidents, by email, to the destination the operator configured — and
 * nobody has chosen. The API sends the flag precisely so a screen need not guess.
 *
 * It **says when a critical incident would reach nobody.** A declared policy can be well-formed,
 * stored, and route nothing critical: a tenant who declared "info volume anomalies to a webhook" and
 * nothing else has silently stopped being paged about a dropped column. Every rule is valid in
 * isolation, so the API cannot say it and the screen must — the failure mode is silence.
 *
 * It **reports coverage per kind rather than as one flag**, because a policy is realistically covered
 * for schema drift and not for a freshness breach, and one boolean would report the whole estate as
 * fine on the strength of a single rule.
 *
 * It **names a tenant-supplied destination as the row an access review looks at.** Not an accusation
 * — it is the one where a customer's incident metadata leaves the destination the operator
 * configured. The deployment default stays neutral, because colouring the ordinary case makes it look
 * like a finding.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useIncidentQueue } from '@/features/incidents';
import type {
  IncidentKind,
  NotificationChannel,
  NotificationRule,
  NotificationRuleWrite,
  NotificationTargetKind,
  RoutingPolicy,
  Severity,
} from '@/shared/api/generated/observability';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SectionHead } from '@/shared/ui/SectionHead';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  useDeclareRule,
  useRoutingPolicy,
  useSetRuleEnabled,
  useWithdrawRule,
} from '../api/routing';
import {
  coveragePerKind,
  coverageSummary,
  destinationOf,
  draftHint,
  draftIsValid,
  EMPTY_DRAFT,
  isDefaultRule,
  keyOf,
  recipientsOf,
  replayAgainst,
  severityNoteKey,
  targetsFor,
} from '../model/routing';
import type { DraftRule, KindCoverage } from '../model/routing';

/** Every incident kind, for the coverage readout.
 *
 * Listed rather than derived from the enum at runtime — TypeScript enums are string unions here, so
 * there is nothing to iterate — and `satisfies` makes an added member a compile error in this file
 * rather than a kind the coverage check silently stops looking at. That is the failure mode the whole
 * coverage readout exists to prevent, so it must not have one of its own.
 */
const KINDS = [
  'schema_drift',
  'freshness_breach',
  'volume_anomaly',
  'assessment_overdue',
  'contract_breach',
] as const satisfies readonly IncidentKind[];

const CHANNELS = ['email', 'webhook'] as const satisfies readonly NotificationChannel[];
const SEVERITIES = ['critical', 'warning', 'info'] as const satisfies readonly Severity[];

/* The headline: whether a critical incident reaches anybody, and in what tone.
 *
 * A left-ruled tinted band, which is the reference's treatment for the one statement it wants read
 * first. Four states, each a different conversation — see `coverageSummary`.
 */
function CoverageHeadline({ policy }: { readonly policy: RoutingPolicy }): ReactElement {
  const { t } = useTranslation();
  const summary = coverageSummary(policy, KINDS);

  return (
    <div
      className={`lq-band lq-band--${summary.tone}`}
      /* `status` rather than `alert`: this is a standing property of the configuration, present on
         load, not an interruption. An assertive region would announce it over whatever a screen
         reader user was doing. */
      role="status"
    >
      <div className="lq-band__title">{t(`routing.headline.${summary.state}`)}</div>
      <div className="lq-band__body">
        {summary.state === 'partial'
          ? t('routing.headline.partialDetail', {
              /* Resolved and joined here rather than handed to `t` as keys. The first version of
                 this screen interpolated the raw key list, putting
                 `routing.incidentKind.freshness_breach` into the banner whose job is to be read. */
              kinds: summary.uncovered
                .map((kind) => t(`routing.kindLower.${kind}`))
                .join(', '),
            })
          : t(`routing.headline.${summary.state}Detail`)}
      </div>
    </div>
  );
}

/** One kind's column in the coverage card: a coloured top rule, the kind, the verdict, the evidence. */
function CoverageColumn({ coverage }: { readonly coverage: KindCoverage }): ReactElement {
  const { t } = useTranslation();
  const tone = coverage.covered ? 'ok' : 'error';

  return (
    <div className={`lq-rt__cov lq-rt__cov--${tone}`}>
      <div className="lq-rt__covKind">{t(`routing.incidentKind.${coverage.kind}`)}</div>
      <div className="lq-rt__covVerdict">
        {/* The mark and the word, never the mark alone. WCAG 1.4.1, and this codebase has shipped a
            colour-only status once already. */}
        <span className="lq-rt__covMark" aria-hidden="true">
          {coverage.covered ? '✓' : '✕'}
        </span>
        <strong>{t(coverage.covered ? 'routing.covered' : 'routing.nobody')}</strong>
      </div>
      <div className="lq-rt__covNote">
        {coverage.covered
          ? /* The destinations, because "covered" with nothing beside it is a claim a reader cannot
               check. The rule count comes first so two rules to one mailbox reads as one intent
               declared twice rather than as two alerts. */
            t('routing.coveredVia', {
              count: coverage.ruleCount,
              destinations: coverage.destinations.join(', '),
            })
          : t('routing.nobodyDetail')}
      </div>
    </div>
  );
}

function RuleRow({
  rule,
  onWithdraw,
  onToggle,
  busy,
}: {
  readonly rule: NotificationRule;
  readonly onWithdraw: (ruleId: string) => void;
  readonly onToggle: (rule: NotificationRule) => void;
  readonly busy: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const isDefault = isDefaultRule(rule);
  const tenantSupplied = rule.target_kind !== 'deployment_default';

  return (
    /* A disabled rule's row is tinted, and its Enabled cell says so in words too. The tint is the
       scan; the word is the information. */
    <tr className={rule.enabled ? undefined : 'lq-rt__rowOff'}>
      <td>
        <span className="lq-tag lq-tag--channel">
          {t(`routing.channel.${rule.channel}`)}
        </span>
      </td>
      <td>
        {t(`routing.severityFloor.${rule.min_severity}`)}
        <div className="lq-cell__secondary">{t(severityNoteKey(rule.min_severity))}</div>
      </td>
      <td>
        {/* Null is "every kind", not "unknown" and not "none". A dash would read as missing
            configuration on the rule that is deliberately broadest. */}
        {rule.incident_kind === null
          ? t('routing.kind.any')
          : t(`routing.incidentKind.${rule.incident_kind}`)}
      </td>
      <td>
        <span
          className={tenantSupplied ? 'lq-tag lq-tag--tenant' : 'lq-tag lq-tag--default'}
          title={t(
            tenantSupplied
              ? 'routing.target.tenantMeaning'
              : 'routing.target.defaultMeaning',
          )}
        >
          {t(`routing.target.${rule.target_kind}`)}
        </span>
        {/* The address itself, in mono, because it is a string somebody compares against a mail
            server's log. Returned as declared: a screen that cannot show what it stored cannot let
            anybody check it. */}
        <div className="lq-rt__dest lq-mono">{destinationOf(rule)}</div>
      </td>
      <td>
        <span className={rule.enabled ? 'lq-rt__on' : 'lq-rt__off'}>
          {t(rule.enabled ? 'routing.enabled' : 'routing.disabled')}
        </span>
        {!rule.enabled && (
          <div className="lq-cell__secondary">{t('routing.disabledNote')}</div>
        )}
      </td>
      <td className="lq-table__actions">
        {/* Nothing to act on for the platform's default: it is not a stored row, so both controls
            would 404 — and a control that fails reads as a broken platform rather than as nothing to
            change. */}
        {isDefault ? (
          <span className="lq-muted" title={t('routing.notWithdrawableMeaning')}>
            {t('routing.notWithdrawable')}
          </span>
        ) : (
          <span className="lq-rowactions">
            <button
              type="button"
              className="lq-rowaction"
              disabled={busy}
              onClick={() => {
                onToggle(rule);
              }}
            >
              {t(rule.enabled ? 'routing.disable' : 'routing.enable')}
            </button>
            <span className="lq-rowactions__rule" aria-hidden="true" />
            <button
              type="button"
              className="lq-rowaction"
              disabled={busy}
              onClick={() => {
                onWithdraw(rule.id);
              }}
            >
              {t('routing.withdraw')}
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}

/** The declare form, inline under the Rules heading as the reference has it.
 *
 * **Inline rather than a dialog**, and the reference is right: the footer reads the draft back in
 * plain words, which only helps if the table it will join is visible above it. A dialog would cover
 * the coverage readout the reader is trying to fix.
 */
function DeclareForm({
  onClose,
  onDeclared,
  allowedDomain,
}: {
  readonly onClose: () => void;
  readonly onDeclared: () => void;
  /** The one mail domain this tenancy may route to, or `null` when none is on record. */
  readonly allowedDomain: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const declare = useDeclareRule();
  const [draft, setDraft] = useState<DraftRule>(EMPTY_DRAFT);

  function set(patch: Partial<DraftRule>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  /* Assembled conditionally rather than with explicit `undefined`s. `exactOptionalPropertyTypes` is
   * on, and the distinction is the point of the flag: an absent `recipients` and one present as
   * `undefined` are different requests, and the API's `additionalProperties: false` refuses a
   * `recipients` key on a deployment-default rule. */
  function write(): NotificationRuleWrite {
    const base = {
      channel: draft.channel,
      min_severity: draft.severity,
      target_kind: draft.targetKind,
      ...(draft.kind === '' ? {} : { incident_kind: draft.kind as IncidentKind }),
    };
    if (draft.targetKind === 'email_recipients') {
      return { ...base, recipients: [...recipientsOf(draft)] };
    }
    if (draft.targetKind === 'webhook_url') {
      return { ...base, webhook_url: draft.webhookUrl.trim() };
    }
    return base;
  }

  const hint = draftHint(draft, allowedDomain);
  const valid = draftIsValid(draft, allowedDomain);

  return (
    <div className="lq-inlineform">
      <div className="lq-inlineform__title">{t('routing.form.title')}</div>
      <p className="lq-inlineform__intro">{t('routing.form.intro')}</p>

      <div className="lq-inlineform__grid3">
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('routing.field.channel')}</span>
          <select
            value={draft.channel}
            onChange={(event) => {
              /* A destination belongs to a channel — the API refuses a webhook URL on an email rule,
                 and the domain makes it unrepresentable — so switching channel resets a
                 tenant-supplied destination rather than composing a request the server refuses. */
              set({
                channel: event.target.value as NotificationChannel,
                targetKind: 'deployment_default',
                recipients: '',
                webhookUrl: '',
              });
            }}
          >
            {CHANNELS.map((option) => (
              <option key={option} value={option}>
                {t(`routing.channel.${option}`)}
              </option>
            ))}
          </select>
          <span className="lq-inlineform__hint">{t('routing.field.channelHint')}</span>
        </label>

        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('routing.field.severity')}</span>
          <select
            value={draft.severity}
            onChange={(event) => {
              set({ severity: event.target.value as Severity });
            }}
          >
            {SEVERITIES.map((option) => (
              <option key={option} value={option}>
                {t(`routing.severityFloor.${option}`)}
              </option>
            ))}
          </select>
          <span className="lq-inlineform__hint">{t('routing.field.severityHint')}</span>
        </label>

        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('routing.field.kind')}</span>
          <select
            value={draft.kind}
            onChange={(event) => {
              set({ kind: event.target.value });
            }}
          >
            <option value="">{t('routing.kind.any')}</option>
            {KINDS.map((option) => (
              <option key={option} value={option}>
                {t(`routing.incidentKind.${option}`)}
              </option>
            ))}
          </select>
          <span className="lq-inlineform__hint">{t('routing.field.kindHint')}</span>
        </label>
      </div>

      <div className="lq-inlineform__grid2">
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('routing.field.target')}</span>
          <select
            value={draft.targetKind}
            onChange={(event) => {
              set({ targetKind: event.target.value as NotificationTargetKind });
            }}
          >
            {targetsFor(draft.channel).map((option) => (
              <option key={option} value={option}>
                {t(`routing.target.${option}`)}
              </option>
            ))}
          </select>
          <span className="lq-inlineform__hint">
            {t(
              draft.targetKind === 'deployment_default'
                ? 'routing.field.targetDefaultHint'
                : 'routing.field.targetTenantHint',
            )}
          </span>
        </label>

        {draft.targetKind === 'email_recipients' && (
          <label className="lq-inlineform__field">
            <span className="lq-inlineform__label">{t('routing.field.recipients')}</span>
            <input
              className="lq-mono"
              type="text"
              value={draft.recipients}
              placeholder={
                allowedDomain === null
                  ? t('routing.field.recipientsPlaceholder')
                  : t('routing.field.recipientsPlaceholderDomain', {
                      domain: allowedDomain,
                    })
              }
              onChange={(event) => {
                set({ recipients: event.target.value });
              }}
            />
            {/* **The restriction, stated where the address is typed.** A reader who has to discover
                it from a 422 has already composed four addresses and had them refused as a set; the
                domain in front of them is what prevents that. `null` is its own sentence rather than
                a silent absence — it means no tenant-supplied address is admissible at all, which is
                a different thing from "any domain". */}
            <span className="lq-inlineform__hint">
              {allowedDomain === null
                ? t('routing.field.recipientsNoDomain')
                : t('routing.field.recipientsDomainHint', { domain: allowedDomain })}
            </span>
          </label>
        )}

        {draft.targetKind === 'webhook_url' && (
          <label className="lq-inlineform__field">
            <span className="lq-inlineform__label">{t('routing.field.webhook')}</span>
            <input
              className="lq-mono"
              type="text"
              value={draft.webhookUrl}
              placeholder={t('routing.field.webhookPlaceholder')}
              onChange={(event) => {
                set({ webhookUrl: event.target.value });
              }}
            />
            <span className="lq-inlineform__hint">{t('routing.field.webhookHint')}</span>
          </label>
        )}
      </div>

      {declare.isError && (
        /* The server's own explanation, verbatim. A destination outside the deployment's allow-list
           comes back naming the variable an operator has to change, and restating it here would be a
           second place deciding what a refusal means. */
        <Alert tone="error" title={t('routing.declareFailed')}>
          {declare.error.message}
        </Alert>
      )}

      <div className="lq-inlineform__foot">
        {/* The draft read back in prose, or the reason it cannot be submitted. This is what catches a
            wrong dropdown — a green tick does not. */}
        <div
          className={
            hint.kind === 'blocked' ? 'lq-inlineform__blocked' : 'lq-inlineform__summary'
          }
        >
          {t(hint.key, {
            ...Object.fromEntries(
              Object.entries(hint.keyed).map(([name, key]) => [name, t(key)]),
            ),
            ...hint.literal,
          })}
        </div>
        <button type="button" className="lq-inlineform__cancel" onClick={onClose}>
          {t('routing.form.cancel')}
        </button>
        <button
          type="button"
          className="lq-inlineform__submit"
          /* Disabled on an invalid draft rather than submitting and reporting a refusal: these two
             rules are ones the browser can check for certain, and a round trip to be told a recipient
             list is empty is a round trip nobody needed. Everything the browser *cannot* know — the
             operator's allow-list — is left to the server. */
          disabled={!valid || declare.isPending}
          onClick={() => {
            declare.mutate(write(), { onSuccess: onDeclared });
          }}
        >
          {t('routing.form.declare')}
        </button>
      </div>
    </div>
  );
}

/** What these rules would have done to the incidents already on the queue.
 *
 * Real incidents and real rules, replayed in the browser through the same `admitsSeverity` and
 * `admitsKind` the coverage readout uses. **Against the rules as they stand now**, which the footnote
 * says plainly: this platform exposes no per-incident delivery record, so a true history would have
 * to be invented, and a reader who took this for a delivery log would be reading a claim nothing
 * behind it supports.
 */
function ReplayTable({ policy }: { readonly policy: RoutingPolicy }): ReactElement {
  const { t } = useTranslation();
  const queue = useIncidentQueue();

  if (queue.isPending) return <SkeletonRows rows={3} columns={4} />;
  if (queue.isError) {
    /* A failed read is not "these rules would reach nobody". Reported as a failure of the request,
       because the alternative is a statement about a customer's alerting that nothing measured. */
    return <Alert tone="error">{t('routing.replay.unreadable')}</Alert>;
  }
  if (queue.data.length === 0) {
    return (
      <EmptyState
        title={t('routing.replay.emptyTitle')}
        guidance={t('routing.replay.empty')}
      />
    );
  }

  const rows = replayAgainst(policy, queue.data);

  return (
    <div className="lq-table-scroll lq-table-scroll--framed">
      <table className="lq-table lq-table--ledger">
        <caption className="lq-visually-hidden">{t('routing.replay.caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('routing.replay.column.incident')}</th>
            <th scope="col">{t('routing.replay.column.severity')}</th>
            <th scope="col">{t('routing.replay.column.kind')}</th>
            <th scope="col">{t('routing.replay.column.reached')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.incidentId}>
              <td className="lq-mono">{row.incidentId.slice(0, 8)}</td>
              <td>
                <span className={`lq-rt__sev lq-rt__sev--${row.severity}`}>
                  {t(`routing.severityName.${row.severity}`)}
                </span>
              </td>
              <td>{t(`routing.incidentKind.${row.kind}`)}</td>
              <td>
                {row.reached.length === 0 ? (
                  /* The one cell on this screen that has to be loud: an incident already on the
                     queue that these rules would tell nobody about. */
                  <span className="lq-rt__nobody">{t('routing.nobody')}</span>
                ) : (
                  <span className="lq-mono">{row.reached.join(', ')}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AlertRoutingScreen(): ReactElement {
  const { t } = useTranslation();
  const policy = useRoutingPolicy();
  const withdraw = useWithdrawRule();
  const setEnabled = useSetRuleEnabled();
  const [formOpen, setFormOpen] = useState(false);
  const [message, setMessage] = useState<{
    readonly titleKey: string;
    readonly bodyKey: string;
  } | null>(null);

  const header = (
    <PageHeader
      title={t('routing.title')}
      lede={t('routing.subtitle')}
      meta={
        policy.isSuccess ? (
          <>
            <div>
              {t(policy.data.declared ? 'routing.meta.declared' : 'routing.meta.default')}
            </div>
            <div>
              {t('routing.meta.ruleCount', { count: policy.data.rules.length })}
              {policy.data.declared ? '' : ` ${t('routing.meta.notStored')}`}
            </div>
          </>
        ) : undefined
      }
    />
  );

  if (policy.isPending) {
    return (
      <div className="lq-routing-page">
        {header}
        <SkeletonRows rows={4} columns={6} />
      </div>
    );
  }

  if (policy.isError) {
    return (
      <div className="lq-routing-page">
        {header}
        <Alert tone="error" title={t('routing.readFailed')}>
          {policy.error.message}
        </Alert>
      </div>
    );
  }

  const rules = policy.data.rules;
  const failure = withdraw.error ?? setEnabled.error;

  return (
    <div className="lq-routing-page">
      {header}
      <div className="lq-pagerule" />

      <CoverageHeadline policy={policy.data} />

      {!policy.data.declared && (
        /* The default is not a decision, and the headline above is about *coverage* rather than about
           who chose it — so this is a second band rather than a clause folded into the first. */
        <div className="lq-band lq-band--neutral">
          <div className="lq-band__title">{t('routing.undeclared')}</div>
          <div className="lq-band__body">{t('routing.undeclaredDetail')}</div>
        </div>
      )}

      {/* Stated on the page as well as in the form: somebody reading the table to work out why an
          alert went nowhere needs the rule without opening a control. */}
      <p className="lq-rt__domain">
        {policy.data.allowed_email_domain === null
          ? t('routing.domain.none')
          : t('routing.domain.only', { domain: policy.data.allowed_email_domain })}
      </p>

      <SectionHead title={t('routing.section.coverage')} />
      <div className="lq-rt__covCard">
        <div className="lq-rt__covGrid">
          {coveragePerKind(policy.data, KINDS).map((coverage) => (
            <CoverageColumn key={coverage.kind} coverage={coverage} />
          ))}
        </div>
        <p className="lq-rt__covFoot">{t('routing.section.coverageFoot')}</p>
      </div>

      <SectionHead
        title={t('routing.section.rules')}
        action={
          <button
            type="button"
            className="lq-sect__action"
            onClick={() => {
              setFormOpen((open) => !open);
              setMessage(null);
            }}
          >
            {t(formOpen ? 'routing.form.close' : 'routing.form.open')}
          </button>
        }
      />

      {message !== null && (
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">{t(message.titleKey)}</strong>
          <span className="lq-flash__body">{t(message.bodyKey)}</span>
        </div>
      )}

      {failure !== null && (
        <Alert tone="error" title={t('routing.actionFailed')}>
          {failure.message}
        </Alert>
      )}

      {formOpen && (
        <DeclareForm
          allowedDomain={policy.data.allowed_email_domain}
          onClose={() => {
            setFormOpen(false);
          }}
          onDeclared={() => {
            setFormOpen(false);
            setMessage({
              titleKey: 'routing.msg.declared',
              bodyKey: policy.data.declared
                ? 'routing.msg.declaredDetail'
                : 'routing.msg.firstDeclaredDetail',
            });
          }}
        />
      )}

      {rules.length === 0 ? (
        /* Unreachable against the live API today, and kept as the honest answer rather than deleted:
           `RoutingService.read` is `if not stored: return default_for(...)`, so zero stored rules
           always answers `declared: false` with the platform default. Proved by withdrawing the last
           rule and watching the default come back. */
        <EmptyState title={t('routing.none')} guidance={t('routing.noneDetail')} />
      ) : (
        <div className="lq-table-scroll lq-table-scroll--framed">
          <table className="lq-table lq-table--ledger">
            <caption className="lq-visually-hidden">{t('routing.caption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('routing.column.channel')}</th>
                <th scope="col">{t('routing.column.severity')}</th>
                <th scope="col">{t('routing.column.kind')}</th>
                <th scope="col">{t('routing.column.destination')}</th>
                <th scope="col">{t('routing.column.enabled')}</th>
                <th scope="col" className="lq-table__actions" />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <RuleRow
                  key={keyOf(rule)}
                  rule={rule}
                  busy={
                    (withdraw.isPending && withdraw.variables === rule.id) ||
                    (setEnabled.isPending && setEnabled.variables.ruleId === rule.id)
                  }
                  onWithdraw={(ruleId) => {
                    withdraw.mutate(ruleId, {
                      onSuccess: () => {
                        setMessage({
                          titleKey: 'routing.msg.withdrawn',
                          bodyKey: 'routing.msg.withdrawnDetail',
                        });
                      },
                    });
                  }}
                  onToggle={(each) => {
                    setEnabled.mutate(
                      { ruleId: each.id, enabled: !each.enabled },
                      {
                        onSuccess: () => {
                          setMessage({
                            titleKey: each.enabled
                              ? 'routing.msg.disabled'
                              : 'routing.msg.enabled',
                            bodyKey: 'routing.msg.toggledDetail',
                          });
                        },
                      },
                    );
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="lq-note">{t('routing.tenantDestinationNote')}</p>

      <SectionHead title={t('routing.section.replay')} />
      <ReplayTable policy={policy.data} />
      <p className="lq-note">{t('routing.replay.foot')}</p>
    </div>
  );
}
